import { makeArticle, notWorkingRelated } from "./learnFactory";

export const pageSignsOfTrd = makeArticle({
  slug: "signs-of-treatment-resistant-depression",
  title: "Signs of Treatment-Resistant Depression | Neuro Wellness",
  description:
    "Signs that depression may be treatment-resistant: inadequate response to antidepressants, persistent symptoms, and when to consider TMS, SAINT®, or SPRAVATO®.",
  canonical: "/learn/signs-of-treatment-resistant-depression",
  h1: "What Are the Signs of Treatment-Resistant Depression?",
  breadcrumbLabel: "Signs of TRD",
  shortAnswer:
    "The primary sign of treatment-resistant depression is inadequate response to at least two antidepressant medication trials of adequate dose and duration in the current episode. Other signs include persistent symptoms despite medication, partial response that plateaus, recurring episodes despite treatment, and side effects that prevent adequate dosing. Treatment-resistant depression is not a separate diagnosis — it describes your treatment history. If you suspect treatment-resistant depression, a psychiatric evaluation can confirm whether past trials were adequate and what to try next.",
  keyTakeaways: [
    "TRD means inadequate response to ≥2 adequate antidepressant trials.",
    "Persistent symptoms despite medication is the key sign.",
    "Partial response that plateaus may indicate TRD.",
    "TRD is a description of treatment history, not a separate diagnosis.",
    "A psychiatric evaluation can confirm whether past trials were adequate.",
  ],
  sections: [
    {
      heading: "The Primary Sign: Inadequate Response to Medication",
      paragraphs: [
        "The primary sign of treatment-resistant depression is that your depression has not improved adequately after at least two antidepressant medication trials of adequate dose and duration. 'Adequate' means the medication was taken at a therapeutic dose (not just a starting dose) consistently for about 6-8 weeks. A medication stopped early because of side effects is usually considered an incomplete trial, not a failed one.",
        "If you've tried two or more antidepressants at adequate doses for adequate durations and your depression hasn't improved, you may meet the definition of treatment-resistant depression.",
      ],
    },
    {
      heading: "Other Signs",
      paragraphs: [
        "Other signs that may indicate treatment-resistant depression include:",
      ],
      list: [
        "Persistent symptoms despite taking medication as prescribed",
        "Partial response that plateaus — some improvement but not full remission",
        "Recurring episodes despite ongoing treatment",
        "Side effects that prevent reaching an adequate dose",
        "Depression that worsens or returns after initial improvement",
        "Multiple medication changes without sustained improvement",
      ],
    },
    {
      heading: "What Treatment-Resistant Depression Is Not",
      paragraphs: [
        "Treatment-resistant depression is not a separate diagnosis — it is a description of your treatment history. It does not mean your depression cannot be treated. It does not mean you've 'failed' treatment. It means that standard first-line treatments (antidepressants) haven't worked adequately, and other options may be appropriate.",
        "It also doesn't mean you need to have tried every available treatment. The definition typically requires at least two failed antidepressant trials, not exhaustion of all options.",
      ],
    },
    {
      heading: "What to Do If You Suspect TRD",
      paragraphs: [
        "If you suspect treatment-resistant depression, ask for a comprehensive psychiatric evaluation. This review confirms whether past medication trials were truly adequate, screens for conditions that can mimic treatment resistance (like bipolar disorder), and identifies what to try next. Options beyond standard antidepressants include TMS, SAINT® accelerated TMS, SPRAVATO® (esketamine), medication optimization, and psychotherapy.",
      ],
    },
  ],
  faqs: [
    {
      question: "What are the signs of treatment-resistant depression?",
      answer:
        "The primary sign is inadequate response to at least two antidepressant trials of adequate dose and duration. Other signs include persistent symptoms despite medication, partial response that plateaus, recurring episodes despite treatment, and side effects that prevent adequate dosing. A psychiatric evaluation can confirm whether past trials were adequate.",
    },
    {
      question: "How do I know if my depression is treatment-resistant?",
      answer:
        "If you've tried two or more antidepressants at adequate doses for 6-8 weeks each and your depression hasn't improved adequately, you may meet the definition. A psychiatric evaluation can review your medication history, confirm whether trials were adequate, and recommend next steps.",
    },
    {
      question: "Is treatment-resistant depression a separate diagnosis?",
      answer:
        "No. Treatment-resistant depression is a description of your treatment history, not a separate diagnosis. It means standard antidepressants haven't worked adequately, and other options may be appropriate. It does not mean your depression cannot be treated.",
    },
    {
      question: "What counts as an adequate antidepressant trial?",
      answer:
        "An adequate trial generally means taking an antidepressant at a therapeutic dose (not just a starting dose) consistently for about 6-8 weeks. A medication stopped early because of side effects is usually considered an incomplete trial, not a failed one.",
    },
    {
      question:
        "What should I do if I think I have treatment-resistant depression?",
      answer:
        "Ask for a comprehensive psychiatric evaluation. This confirms whether past trials were adequate, screens for conditions that can mimic treatment resistance, and identifies what to try next. Options include TMS, SAINT®, SPRAVATO®, medication optimization, and psychotherapy.",
    },
  ],
  sources: [
    {
      citation:
        "Fava M. Diagnosis and definition of treatment-resistant depression. Biol Psychiatry. 2003;53(8):649-659.",
      url: "https://pubmed.ncbi.nlm.nih.gov/12706951/",
    },
  ],
  relatedLinks: notWorkingRelated,
});
